import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { AnimatePresence } from 'motion/react';
import { Sidebar } from './Sidebar.tsx';
import { TopBar } from './TopBar.tsx';
import { PageTransition } from './motion.tsx';
import { LiveStreamProvider, useLiveStreamConnection } from './LiveStream.tsx';

interface Props {
  activeStackTeamId: string | null;
  onChangeTenant: (id: string | null) => void;
  children: ReactNode;
}

const titleByPath: Record<string, string> = {
  '/': 'Dashboard',
  '/hotel': 'Hotel',
  '/doors': 'Doors',
  '/events': 'Events',
  '/cardholders': 'Cardholders',
  '/access-levels': 'Access levels',
  '/schedules': 'Schedules',
  '/holidays': 'Holidays',
  '/controllers': 'Controllers',
  '/zones': 'Zones & alarms',
  '/settings': 'Settings',
  '/developer': 'Developer',
};

function ShellInner({ activeStackTeamId, onChangeTenant, children }: Props) {
  const loc = useLocation();
  const title =
    titleByPath[loc.pathname] ??
    Object.entries(titleByPath).find(([k]) => k !== '/' && loc.pathname.startsWith(k))?.[1] ??
    'CDVI Edge';
  const { connected } = useLiveStreamConnection();

  // Mobile off-canvas nav drawer. Close on navigation and on Escape.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [loc.pathname]);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setNavOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className={`app${navOpen ? ' nav-open' : ''}`}>
      <Sidebar activeStackTeamId={activeStackTeamId} onChangeTenant={onChangeTenant} />
      <TopBar
        title={title}
        liveConnected={connected}
        navOpen={navOpen}
        onMenuToggle={() => setNavOpen((v) => !v)}
      />
      <div className="nav-scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
      <main className="main">
        <AnimatePresence mode="wait" initial={false}>
          <PageTransition key={loc.pathname}>{children}</PageTransition>
        </AnimatePresence>
      </main>
    </div>
  );
}

export function AppShell({ activeStackTeamId, onChangeTenant, children }: Props) {
  const [tenantKey, setTenantKey] = useState(activeStackTeamId);
  useEffect(() => setTenantKey(activeStackTeamId), [activeStackTeamId]);
  return (
    // Re-mounts the live stream subscription when the active tenant changes.
    <LiveStreamProvider key={tenantKey ?? 'none'} enabled={!!activeStackTeamId}>
      <ShellInner activeStackTeamId={activeStackTeamId} onChangeTenant={onChangeTenant}>
        {children}
      </ShellInner>
    </LiveStreamProvider>
  );
}
